import { ColDef, FilterChangedEvent, GridOptions, GridReadyEvent, RowSelectedEvent } from '@ag-grid-community/core';
import { Component, OnDestroy, OnInit, Optional, ViewEncapsulation } from '@angular/core';

import { AgStyleFns } from '@/shared/ag-style-fns';
import { UniAgFns } from '@/shared/uni-ag-fns';
import { ActivatedRoute } from '@angular/router';
import { ManufacturerFinderDialogComponent } from 'app/inventory/manufacturer-finder-dialog.component';
import { PoDetailTypeCode } from 'app/model/enums/po-detail-type-code';
import { PoStatusCode } from 'app/model/enums/po-status-code';
import { AgFns, GridOptionsExt, ISortModel } from 'app/shared/ag-fns';
import { AgButtonProps } from 'app/shared/button-renderer.component';
import { DialogBaseComponent } from 'app/shared/dialog-base.component';
import { EditNotesDialogComponent } from 'app/shared/edit-notes-dialog.component';
import { EntityFinderDialog } from 'app/shared/entity-finder.dialog';
import { NavFns } from 'app/shared/nav-fns';
import { PoUtilsService } from 'app/shared/po-utils.service';
import _ from 'lodash';
import { DialogService } from 'primeng/dynamicdialog';
import { Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  Account,
  ItemDetail,
  JoHeader,
  Manufacturer,
  PoDetail,
  PoDetailCancel,
  PoHeader,
  PoNote,
  PoStatus,
  Product,
} from '../model/entities/entity-model';
import { DomainBaseComponent, wrapBusy } from '../shared/domain-base.component';
import { DomainService } from '../shared/domain.service';
import { AddPodsDialogComponent } from './add-pods-dialog.component';
import { CombinePurchaseOrdersDialogComponent } from './combine-purchase-orders-dialog.component';
import { EditPoInstructionsDialogComponent } from './edit-po-instructions-dialog.component';
import { EditPodDialogComponent } from './edit-pod-dialog.component';
import { PoPrintDialogComponent } from './po-print-dialog.component';
import { PurchaseManagerComponent } from './purchase-manager.component';
import { SplitPurchaseOrdersDialogComponent } from './split-purchase-orders-dialog.component';

class PodGroup {
  origProductId: number;
  origProduct?: Product;
  productId: number;
  product: Product;
  qty: number;
  heldQty: number;
  receivedQty: number;
  canceledQty: number;
  poDetails: PoDetail[];
  expectedReceiveDate: Date;
  isProcessed: boolean;
}

@Component({
  selector: 'app-purchase-orders',
  templateUrl: './purchase-orders.component.html',

  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class PurchaseOrdersComponent extends DomainBaseComponent implements OnInit, OnDestroy {
  poHeaders!: PoHeader[];
  poGridOptions!: GridOptionsExt;

  podGroups: PodGroup[] = [];
  podGroupGridOptions!: GridOptionsExt;

  jobOrders: JoHeader[] = [];
  joGridOptions!: GridOptionsExt;

  allStatuses!: PoStatus[];
  allStatusesExt!: PoStatus[];
  nullPoStatus = <PoStatus>{ id: '', name: 'All' };
  completedOpenStatus = <PoStatus>{ id: 'COMPLETED_OPEN', name: 'Completed w/ Open Qty' };
  selectedStatuses: PoStatus[] = [this.nullPoStatus];

  currentPoHeader!: PoHeader;

  isUnpostedOnly!: boolean;
  isOpenOnly!: boolean;
  isCompletedOnly!: boolean;
  isCompletedWithOpenQtyOnly!: boolean;
  currentDate = new Date();

  get canForceCancel(): boolean {
    return this.isCompletedOnly || this.isCompletedWithOpenQtyOnly;
  }

  private createNewPoSubscription!: Subscription;

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute,
    public pngDialogService: DialogService,
    private poUtilsService: PoUtilsService,
    @Optional() private purchaseManager: PurchaseManagerComponent,
  ) {
    super(domainService);

    this.route.queryParamMap.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.updateFromContext();
    });

    // Subscribe to parent's create new PO button click
    if (this.purchaseManager) {
      this.createNewPoSubscription = this.purchaseManager.createNewPoClicked$.subscribe(() => {
        this.createNewPo();
      });
    }
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    if (this.createNewPoSubscription) {
      this.createNewPoSubscription.unsubscribe();
    }
    // Hide the button when leaving this component
    if (this.purchaseManager) {
      this.purchaseManager.showCreateNewPoButton = false;
    }
  }

  async updateFromContext() {
    // this.gridState = AgFns.createGridState(this.route.snapshot.queryParams, 'id');
    this.allStatuses = this.dbQueryService.getAllCached(PoStatus);
    this.allStatusesExt = this.allStatuses.slice();
    this.allStatusesExt.unshift(this.nullPoStatus);
    this.allStatusesExt.push(this.completedOpenStatus);

    this.poGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onPoGridReady,
      onRowSelected: this.onRowSelected,
      onModelUpdated: this.onPoModelUpdated,
      onFilterChanged: this.onFilterChanged,
      rowModelType: 'serverSide',
      getRowClass: (params) => {
        return (params.data as any)?._isForceCancelProcessed ? 'rs-already-processed' : '';
      },
      onPaginationChanged: (params) => {
        if (params.newPage) {
          this.clearCurrentSelection();
        }
      },
    });

    this.captureLocation(this.poGridOptions, 'id');

    if (this.poGridOptions.context?.gridState?.fm == null) {
      this.selectedStatuses = [this.nullPoStatus];
    }

    this.podGroupGridOptions = AgFns.initGridOptions(
      this,
      {
        onGridReady: this.onPodGridReady,
        getRowId: (x) => {
          return (x.data as PodGroup).origProductId.toString();
        },
      },
      { detailProperty: 'poDetails' },
    );
    this.updatePodMasterDetail(this.podGroupGridOptions);

    this.joGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onJoGridReady,
      cellSelection: false,
    });
    this.joGridOptions.context.name = 'joGridOptions';

    this.isPageReady = true;
  }

  override async canDeactivate() {
    const ok = await super.canDeactivate();
    if (!ok) return false;
    this.uow.clearEntities(PoHeader);
    this.uow.clearEntities(PoDetail);
    this.uow.clearEntities(Account);
    this.uow.clearEntities(Manufacturer);
    this.uow.clearEntities(ItemDetail);
    this.uow.clearEntities(PoNote);
    this.uow.clearEntities(PoDetailCancel);
    return true;
  }

  // This method is called AFTER ngAfterViewInit - this is important
  // because the ColDef's here reference TemplateRef's above that will not be resolved until here.
  onPoGridReady(event: GridReadyEvent) {
    const receiveOpbuttonProps: AgButtonProps = {
      calcDisabled: this.areReceiveOpsDisabled.bind(this),
    };

    const colDefs = [
      {
        ...AgFns.createCheckboxProps('', '_isForceCancelChecked', 'forceCancelCheckbox'),
        valueGetter: (p: any) => !!p.data?._isForceCancelChecked,
        valueSetter: (p: any) => {
          if (!p.data) return false;
          p.data._isForceCancelChecked = !!p.newValue;
          return true;
        },
        editable: true,
        sortable: false,
        filter: false,
        hide: true,
        ...AgFns.fixWidth(50),
      },
      {
        headerName: 'Purchase Order',
        field: 'id',
        filter: 'agNumberColumnFilter',
        type: 'leftAligned',
        ...AgFns.fixWidth(160),
      },
      {
        headerName: 'Manufacturer',
        field: 'manufacturer.name',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(300),
      },
      {
        ...AgFns.createSetFilterObjectProps('Status', 'poStatus.name', this.allStatuses),
        ...AgFns.fixWidth(220),
      },
      {
        headerName: 'P/O Date',
        field: 'poDate',
        filter: 'agDateColumnFilter',
        ...AgFns.fixWidth(140),
      },
      {
        headerName: 'Exp. Receive Date',
        field: 'origExpectedReceiveDate',
        filter: 'agDateColumnFilter',
        cellClass: (params: any) => AgStyleFns.getDueDateCellClass(params.value),
        ...AgFns.fixWidth(180),
      },
      {
        ...AgFns.createButtonProps('', this.lockPo.bind(this), {
          colId: 'isLocked',
          label: 'Lock',
          calcLabel: this.calcLockPoLabel.bind(this),
          ...receiveOpbuttonProps,
        }),
        ...AgFns.fixWidth(90),
      },
      {
        ...AgFns.createButtonProps('', this.combine.bind(this), { colId: 'combine', label: 'Combine', ...receiveOpbuttonProps }),
        ...AgFns.fixWidth(90),
      },
      {
        ...AgFns.createButtonProps('', this.split.bind(this), { colId: 'split', label: 'Split', ...receiveOpbuttonProps }),
        ...AgFns.fixWidth(90),
      },
      {
        ...AgFns.createButtonProps('', this.receivePo.bind(this), { colId: 'receive', label: 'Receive', ...receiveOpbuttonProps }),
        ...AgFns.fixWidth(90),
      },
      {
        ...AgFns.createButtonProps('', this.editPoNotes.bind(this), {
          label: 'Notes',
          calcClass: this.calcEditPoNotesClass.bind(this),
        }),
        ...AgFns.fixWidth(90),
      },
      {
        ...AgFns.createButtonProps('', this.editPoInstructions.bind(this), {
          label: 'Instructions',
          calcClass: this.calcEditPoInstructionsClass.bind(this),
        }),
        ...AgFns.fixWidth(110),
      },
      {
        ...AgFns.createButtonProps('', this.printPo.bind(this), { colId: 'print', label: 'Print', ...receiveOpbuttonProps }),
        ...AgFns.fixWidth(90),
      },
      {
        ...AgFns.createButtonProps('', this.addPod.bind(this), {
          colId: 'addStock',
          label: `Add Stock Detail`,
          ...receiveOpbuttonProps,
        }),
        ...AgFns.fixWidth(130),
      },
      {
        ...AgFns.createButtonProps('', this.onDelete.bind(this), {
          colId: 'delete',
          label: 'Delete',
          ...receiveOpbuttonProps,
        }),
        ...AgFns.fixWidth(90),
      },
    ];

    const sortModel = [
      { colId: 'manufacturer.name', sort: 'asc' },
      { colId: 'id', sort: 'desc' },
    ] as ISortModel;

    // next line is needed by the buildDatasource method
    AgFns.initGrid(event, colDefs, sortModel);

    // This needs to be done last in order to avoid excess query calls
    this.updateDatasource();
    this.applyLocation();

    this.updateColumnVisibility();
  }

  areReceiveOpsDisabled() {
    return !this.authUser.isReceiveAdmin;
  }

  onPodGridReady(event: GridReadyEvent) {
    const colDefs: ColDef[] = [
      {
        ...AgFns.createButtonProps('', this.noop.bind(this), {
          icon: 'pi pi-info-circle',
          canDisplay: (podg: PodGroup) => this.isAccountOwnedPodGroup(podg),
          label: '',
          tooltip: 'Account-owned inventory order',
          colId: 'accountOwnedIndicator',
        }),
        headerName: '',
        sortable: false,
        ...AgFns.fixWidth(45),
      },
      { ...UniAgFns.getManufacturerColDef('product.productType.manufacturer.name'), cellRenderer: 'agGroupCellRenderer' },
      {
        headerName: 'SKU',
        field: 'product.productType.style',
        ...NavFns.createCellClickedCalcNavProps(this.router, (event) => {
          const podg = <PodGroup>event.data;
          return { navPath: `/references/manage/product-type/${podg.product.productTypeId}` };
        }),
        ...AgFns.flexWidth(150),
      },
      { ...UniAgFns.getProductTypeDescriptionColDef('product.productType.description') },
      { ...UniAgFns.getFeatureChoicesColDef('product.featureChoicesExtract') },

      {
        ...AgFns.createButtonProps('', this.onUpdateToReorderProduct.bind(this), {
          colId: 'updateForReorder',
          label: 'Update for reorder',
          calcLabel: this.getProductChangeLabel.bind(this),
          calcDisabled: this.isProductChangeDisabled.bind(this),
        }),
        ...AgFns.fixWidth(120),
      },
      {
        headerName: 'Orig. Features',
        valueGetter: (params) => {
          const podg = <PodGroup>params.data;
          if (podg.productId == podg.origProductId) {
            return '[same]';
          } else {
            return podg.origProduct!.getFeatureChoicesExtract();
          }
        },
        ...AgFns.flexWidth(150),
      },
      {
        headerName: 'Order Qty',
        colId: 'qty',
        field: 'qty',
        type: 'numericColumn',
        ...AgFns.fixWidth(120),
      },
      {
        headerName: 'Held Qty',
        field: 'heldQty',
        colId: 'heldQty',
        type: 'numericColumn',
        ...AgFns.fixWidth(120),
      },
      {
        headerName: "Rec'd Qty",
        field: 'receivedQty',
        colId: 'receivedQty',
        type: 'numericColumn',
        ...AgFns.fixWidth(120),
      },
      {
        headerName: 'Cancel Qty',
        field: 'canceledQty',
        colId: 'canceledQty',
        type: 'numericColumn',
        ...AgFns.fixWidth(120),
      },
      {
        headerName: 'Open Qty',
        type: 'numericColumn',
        sortable: true,
        valueGetter: (params) => {
          const podg = <PodGroup>params.data;
          return podg.qty - podg.canceledQty - podg.receivedQty;
        },
        ...AgFns.fixWidth(120),
      },
      {
        headerName: 'Exp. Receive Date',
        field: 'expectedReceiveDate',
        valueGetter: (params) => {
          const podg = <PodGroup>params.data;
          const hasOpen = podg.qty - podg.canceledQty - podg.receivedQty > 0;
          if (hasOpen) {
            return params.data.expectedReceiveDate;
          } else {
            return null;
          }
        },
        cellClass: (params) => {
          const pog = params.data as PodGroup;
          return AgStyleFns.getPodDueDateCellClass(pog.isProcessed, pog.qty - pog.canceledQty - pog.receivedQty > 0, params.value);
        },
        ...AgFns.fixWidth(180),
      },
    ];
    const sortFields = ['product.productType.style', 'product.getSortOrder()'];
    const sortModel = sortFields.map((sf) => {
      return { colId: sf, sort: 'asc' as const };
    });

    this.podGroupGridOptions.getRowClass = function (params) {
      const pod = params.node.data as PodGroup;
      if (pod && pod.qty - (pod.receivedQty + pod.canceledQty) <= 0) {
        return 'rs-completed';
      }
    };

    AgFns.initGrid(event, colDefs, sortModel);
  }

  isProductChangeDisabled(podGroup: PodGroup) {
    if (podGroup.poDetails.some((pod) => pod.joDetail?.joHeader?.isAccountOwnedOrder)) return true;
    if (podGroup.origProductId != podGroup.productId) {
      return false;
    }

    if (!podGroup.product?.productType.allowReorderFeatureChoices) {
      return true;
    }
    if (podGroup.product.productFeatureChoices.every((pfc) => pfc.featureChoice.reorderFeatureChoiceId == null)) {
      return true;
    }
    return false;
  }

  getProductChangeLabel(podGroup: PodGroup) {
    if (podGroup.origProductId == podGroup.productId) {
      return 'Update';
    } else {
      return 'Revert';
    }
  }

  updatePodMasterDetail(parentGridOptions: GridOptions) {
    const detailGridOptions = AgFns.createDetailGridOptions();
    detailGridOptions.columnDefs = [
      { headerName: 'P/O Detail Id', field: 'id', width: 90 },
      {
        headerName: 'Exp. Ship Date',
        field: 'expectedShipDate',
        width: 125,
      },
      {
        headerName: 'Exp. Receive Date',
        field: 'expectedReceiveDate',
        width: 125,
      },
      { headerName: 'Type', field: 'poDetailType.name', width: 80 },

      {
        ...AgFns.createButtonProps('', this.editPod.bind(this), {
          label: 'Edit',
          canDisplay: this.canEditPod.bind(this),
        }),
      },
      {
        ...AgFns.createButtonProps('', this.deletePod.bind(this), {
          label: 'Delete',
          canDisplay: this.canDeletePod.bind(this),
        }),
      },
      {
        ...AgFns.createButtonProps('', this.editPodNotes.bind(this), {
          label: 'Notes',
          calcClass: (row) => (this.hasPodNotes(row) ? 'btn-has-data' : ''),
        }),
      },

      {
        headerName: 'Order Qty',
        field: 'orderQty',
        width: 80,
        type: 'numericColumn',
      },
      {
        headerName: 'Held Qty',
        field: 'heldQty',
        width: 80,
        type: 'numericColumn',
      },
      {
        headerName: 'Received',
        width: 80,
        type: 'numericColumn',
        valueGetter: (params) => {
          const pod = <PoDetail>params.data;
          return pod.getReceivedQty();
        },
      },
      {
        headerName: 'Canceled',
        width: 80,
        type: 'numericColumn',
        valueGetter: (params) => {
          const pod = <PoDetail>params.data;
          return pod.getCanceledQty();
        },
      },
      // { headerName: "Job Order Detail", field: "joDetailId", width: 100 },
      {
        headerName: 'Job Order',
        ...NavFns.createIdCellClickedNavProps('joDetail.joHeaderId', this.router, '/in-process/manage/job-orders'),
        ...AgFns.fixWidth(120),
      },
      {
        headerName: 'J/O Status',
        field: 'joDetail.joHeader.joStatus.name',
        ...AgFns.fixWidth(120),
      },
      {
        headerName: 'J/O Detail Exp. Ship By',
        field: 'joDetail.expectedShipDate',
        width: 90,
      },
      {
        headerName: 'Account',
        field: 'joDetail.joHeader.account.accountName',
        width: 200,
      },
    ];

    AgFns.updateColDefs(detailGridOptions.columnDefs);
    parentGridOptions.detailCellRendererParams = {
      detailGridOptions: detailGridOptions,
      getDetailRowData: (params: any) => {
        const podGroup = params.data as PodGroup;
        params.successCallback(podGroup.poDetails);
      },
    };
  }

  onJoGridReady(event: GridReadyEvent) {
    const colDefs = [
      {
        headerName: 'Job Order',
        ...NavFns.createIdCellClickedNavProps('id', this.router, '/in-process/manage/job-orders'),
        ...AgFns.fixWidth(180),
      },
      { headerName: 'Account', field: 'account.accountName', ...AgFns.flexWidth(200) },
      { headerName: 'Shipping Name', field: 'shippingName', ...AgFns.flexWidth(200) },
      { headerName: 'Shipping Address 1', field: 'shippingAddress1', ...AgFns.flexWidth(200) },
      {
        ...AgFns.createCheckboxProps('Acct Owned?', 'isAccountOwnedOrder'),
        editable: false,
        ...AgFns.fixWidth(180),
      },
      { headerName: 'J/O Date', field: 'joDate', ...AgFns.fixWidth(160) },
      {
        headerName: 'Orig. Ship By Date',
        field: 'origExpectedShipDate',
        cellClass: (params: any) => AgStyleFns.getDueDateCellClass(params.value),
        ...AgFns.fixWidth(160),
      },
      { headerName: 'J/O Status', field: 'joStatus.name', ...AgFns.fixWidth(250) },
    ];
    const sortModel = [
      { colId: 'account.accountName', sort: 'asc' },
      { colId: 'joDate', sort: 'asc' },
    ] as ISortModel;
    AgFns.initGrid(event, colDefs, sortModel);
  }

  get buttonPoStatus() {
    const statuses = this.selectedStatuses;
    return statuses && statuses.length === 1 ? statuses[0] : this.nullPoStatus;
  }
  set buttonPoStatus(value: PoStatus) {
    if (value == null || value == this.buttonPoStatus) {
      return;
    }

    this.filterByPoStatus([value]);
  }

  @wrapBusy
  private async filterByPoStatus(poStatuses: PoStatus[]) {
    const apiX = this.poGridOptions.apiX;
    if (apiX == null) {
      return;
    }

    const isCompletedOpen = poStatuses.length === 1 && poStatuses[0] === this.completedOpenStatus;

    if (poStatuses.length > 0 && poStatuses[0] === this.nullPoStatus) {
      poStatuses = [];
    }
    this.selectedStatuses = poStatuses;

    if (isCompletedOpen) {
      if (apiX.getColumnFilterModel('poStatus.name') != null) {
        await apiX.setColumnFilterModel('poStatus.name', null);
      }
      this.selectedStatuses = poStatuses;
      this.updateColumnVisibility();
      this.updateDatasource();
      return;
    }

    if (poStatuses.length === 0) {
      const cfm = apiX.getColumnFilterModel('poStatus.name');
      if (cfm != null) {
        await apiX.setColumnFilterModel('poStatus.name', null);
      }
    } else {
      await apiX.setColumnFilterModel('poStatus.name', {
        filterType: 'set',
        values: poStatuses.map((pos) => pos.name),
      });
    }

    this.selectedStatuses = poStatuses;
    this.updateColumnVisibility();
    this.updateDatasource();
    apiX.onFilterChanged();
  }

  onPoModelUpdated() {
    this.clearCurrentSelection();
  }

  async onUpdateToReorderProduct(podGroup: PodGroup) {
    if (podGroup.productId == podGroup.origProductId) {
      await this.dbQueryService.getProductType(podGroup.product.productTypeId);
      podGroup.product = podGroup.origProduct!.getReorderProduct();
      podGroup.productId = podGroup.product.id;
      if (podGroup.productId == podGroup.origProductId) {
        await this.dialogService.showOkMessage('No Reorder Feature Choices', 'There are no reorder feature choices for this product.');
      } else {
        podGroup.poDetails.forEach((x) => (x.productId = podGroup.productId));
        this.dbSaveService.saveSelectedChanges(podGroup.poDetails);
      }
    } else {
      podGroup.product = <any>podGroup.origProduct;
      podGroup.productId = podGroup.origProductId;
      podGroup.poDetails.forEach((x) => (x.productId = podGroup.origProductId));
      this.dbSaveService.saveSelectedChanges(podGroup.poDetails);
    }
    AgFns.refreshGrid(this.podGroupGridOptions, this.podGroups);
  }

  onFilterChanged(e: FilterChangedEvent) {
    // A query-param change rebuilds poGridOptions; the replaced grid can still fire this before the new one is ready.
    const apiX = this.poGridOptions.apiX;
    if (apiX == null || e.api !== apiX) {
      return;
    }
    const model = <any>apiX.getColumnFilterModel('poStatus.name');
    const selectedStatusNames = <string[]>(model ? model.values : []);
    if (selectedStatusNames.length === 0) {
      const inCompletedOpen = this.selectedStatuses.length === 1 && this.selectedStatuses[0] === this.completedOpenStatus;
      if (!inCompletedOpen) {
        this.selectedStatuses = [this.nullPoStatus];
      }
    } else {
      this.selectedStatuses = <any>selectedStatusNames.map((sn) => this.allStatuses.find((pos) => pos.name === sn));
    }
    this.clearCurrentSelection();
    this.updateColumnVisibility();
  }

  clearCurrentSelection() {
    // insure that bound vars are cleared after any sorts or filters. - because selection is lost

    // HACK:
    // this insures that we don't reenter here after the deselectAll call below which causes a modelUpdated event
    // which calls this.  The problem is when returning to the page this method is called before the 'key' row is selected
    // but then is called again after the row is selected ( because deselectAll causes modelUpdated after a timeout) and this
    // 2nd call clears the row that has just been selected. UGH.
    if (this.currentPoHeader === null) {
      return;
    }
    this.currentPoHeader = <any>null;

    this.podGroups = [];
    this.jobOrders = [];

    this.poGridOptions.apiX?.deselectAll();
  }

  updateColumnVisibility() {
    this.isUnpostedOnly = this.selectedStatuses.length === 1 && this.selectedStatuses[0].id === PoStatusCode.NewUnposted;
    this.isOpenOnly = this.selectedStatuses.length === 1 && this.selectedStatuses[0].id === PoStatusCode.OpenIncomplete;
    this.isCompletedOnly = this.selectedStatuses.length === 1 && this.selectedStatuses[0].id === PoStatusCode.Complete;
    this.isCompletedWithOpenQtyOnly = this.selectedStatuses.length === 1 && this.selectedStatuses[0] === this.completedOpenStatus;

    const poGridApi = this.poGridOptions.apiX;
    if (poGridApi) {
      poGridApi.setColumnsVisible(['combine'], this.isUnpostedOnly);
      poGridApi.setColumnsVisible(['split'], this.isUnpostedOnly);
      poGridApi.setColumnsVisible(['isLocked'], this.isUnpostedOnly);
      poGridApi.setColumnsVisible(['addStock'], this.isUnpostedOnly);
      poGridApi.setColumnsVisible(['delete'], this.isUnpostedOnly);
      poGridApi.setColumnsVisible(['receive'], this.isOpenOnly);
      poGridApi.setColumnsVisible(['origExpectedReceiveDate'], this.isOpenOnly);
      poGridApi.setColumnsVisible(['forceCancelCheckbox'], this.isCompletedOnly || this.isCompletedWithOpenQtyOnly);
      AgFns.sizeColumnsToFit(this.poGridOptions);
    }
    const podGroupGridApi = this.podGroupGridOptions.apiX;
    if (podGroupGridApi) {
      podGroupGridApi.setColumnsVisible(['updateForReorder'], this.isUnpostedOnly);
      AgFns.sizeColumnsToFit(this.podGroupGridOptions);
    }

    // Update parent's Create New Stock P/O button visibility
    if (this.purchaseManager) {
      setTimeout(() => {
        this.purchaseManager.showCreateNewPoButton = this.isUnpostedOnly;
      });
    }
  }

  updateDatasource(returnFn?: (r: PoHeader[]) => void) {
    const gridApi = this.poGridOptions.apiX;
    if (gridApi == null) {
      return;
    }
    const opts = {
      returnFn: returnFn,
    };
    const queryFactory = this.isCompletedWithOpenQtyOnly
      ? () => this.dbQueryService.createCompletedPoHeadersWithOpenQtyQuery()
      : () => this.dbQueryService.createPoHeaderQuery(<any>null);
    const ds = AgFns.buildDatasource(queryFactory, <any>null, opts);
    gridApi.setGridOption('serverSideDatasource', ds);
  }

  refreshDatasource() {
    const returnFn = async (r: PoHeader[]) => {
      if (this.currentPoHeader != null) {
        await this.queryPoDetails(this.currentPoHeader, true);

        const rowNode = this.poGridOptions.apiX!.getRowNode(this.currentPoHeader.id.toString());
        if (rowNode != null) {
          this.poGridOptions.apiX!.ensureIndexVisible(<any>rowNode.rowIndex, 'middle');
          rowNode.setSelected(true);
        }
      }
    };
    this.updateDatasource(returnFn);
  }

  getTitle() {
    const names = this.selectedStatuses.map((pos) => pos.name);
    if (names && names.length > 0) {
      return ' - ' + names.join(', ');
    } else {
      return ' - All statuses';
    }
  }

  async onRowSelected(e: RowSelectedEvent) {
    // check if a deselect event and ignore
    if (!e.node.isSelected()) {
      return;
    }
    const po = e.data as PoHeader;
    if (!po) {
      return;
    }
    this.currentPoHeader = po;

    this.replaceLocation(po.id);

    await this.queryPoDetails(po);
    const gridApi = this.podGroupGridOptions.apiX;
    if (gridApi) {
      gridApi.setColumnsVisible(['edit'], this.isUnpostedOnly);
    }
  }

  calcLockPoLabel(poh: PoHeader) {
    return poh.isLocked ? 'Unlock' : 'Lock';
  }

  async lockPo(poh: PoHeader) {
    await AgFns.busyGrid([this.poGridOptions], this, async () => {
      poh.isLocked = !poh.isLocked;
      await this.dbSaveService.saveSelectedChanges([poh]);
    });
  }

  @wrapBusy
  async queryPoDetails(poh: PoHeader, shouldForceQuery = false) {
    return await AgFns.busyGrid([this.podGroupGridOptions, this.joGridOptions], this, () => this.queryPoDetailsCore(poh, shouldForceQuery));
  }

  async queryPoDetailsCore(poh: PoHeader, shouldForceQuery = false) {
    // Can't use breeze.isNavigationLoaded here because we need to check for deeper
    // navigation than just joDetails.
    let poDetails: PoDetail[];

    if (shouldForceQuery || !poh['arePoDetailsLoaded']) {
      poDetails = await this.dbQueryService.getPoDetailsExpanded(poh.id);
      poh['arePoDetailsLoaded'] = true;
    } else {
      poDetails = poh.poDetails;
    }
    // group poDetails into PodGroups
    const podGroups = [] as PodGroup[];
    poDetails.forEach((pod) => {
      let podGroup = podGroups.find((x) => x.origProductId === pod.origProductId);
      if (!podGroup) {
        podGroup = {
          origProductId: pod.origProductId,
          origProduct: pod.origProduct,
          productId: pod.productId, // used to insure uniqueness,
          product: pod.product,
          qty: 0,
          heldQty: 0,
          receivedQty: 0,
          canceledQty: 0,
          poDetails: [],
          expectedReceiveDate: <any>pod.expectedReceiveDate,
          isProcessed: poh.isProcessed(),
        };
        podGroups.push(podGroup);
      }
      podGroup!.poDetails.push(pod);
      podGroup!.qty += pod.orderQty;
      podGroup!.heldQty += pod.heldQty;
      podGroup!.receivedQty += pod.getReceivedQty();
      podGroup!.canceledQty += pod.getCanceledQty();
    });

    this.podGroups = podGroups;
    this.jobOrders = _.chain(poDetails)
      .filter((pod) => pod.joDetail != null)
      .map((pod) => pod.joDetail.joHeader)
      .uniq()
      .value();

    this.podGroupGridOptions?.apiX?.setColumnsVisible(['updateForReorder'], this.isUnpostedOnly);
  }

  @wrapBusy
  async combine(poh: PoHeader) {
    if (this.isAccountOwnedPo(poh)) {
      await this.dialogService.showOkMessage('Cannot Combine', 'Account-owned inventory orders cannot be combined.');
      return;
    }
    const r = await DialogBaseComponent.open(CombinePurchaseOrdersDialogComponent, this, {
      manufacturer: poh.manufacturer,
      destPoHeader: poh,
    });
    if (r) {
      await this.queryPoDetails(poh, true);
      this.refreshDatasource();
    }
    return <boolean>r;
  }

  @wrapBusy
  async onDelete(po: PoHeader) {
    // query for a new PO since po.poDetails.length sometimes returns false zero due to timing issues
    const deletePo = await this.dbQueryService.getPoHeader(po.id);
    if (deletePo!.poDetails.length > 0) {
      this.toastr.warning('Cannot delete a purchase order with details', 'Cannot Delete');
      return null;
    }

    const ok = await this.dialogService.yesNoDialog('Delete this purchase order?', 'Are you sure?');
    if (!ok) return;

    deletePo!.poNotes.forEach((p) => {
      deletePo!.entityAspect.setDeleted();
    });
    deletePo!.entityAspect.setDeleted();
    const sr = await this.dbSaveService.saveChanges();
    if (sr.httpResponse.statusText == 'OK') {
      this.toastr.success('Purchase order successfully deleted');
      this.refreshDatasource();
    } else {
      this.toastr.error('Purchase order delete failed', 'Unable to Delete');
    }
  }

  @wrapBusy
  async split(poh: PoHeader) {
    if (this.isAccountOwnedPo(poh)) {
      await this.dialogService.showOkMessage('Cannot Split', 'Account-owned inventory orders cannot be split.');
      return;
    }
    // insure that all of the details have been queried - removed 4/11/22
    // await this.queryPoDetails(poh, true);
    const r = await DialogBaseComponent.open(SplitPurchaseOrdersDialogComponent, this, { poHeader: poh });
    if (r) {
      this.refreshDatasource();
    }
    return <boolean>r;
  }

  calcEditPoNotesClass(poh: PoHeader) {
    return this.hasPoNotes(poh) ? 'btn-has-data' : '';
  }

  calcEditPoInstructionsClass(poh: PoHeader) {
    return this.hasPoInstructions(poh) ? 'btn-has-data' : '';
  }

  hasPoInstructions(poh: PoHeader) {
    return poh && poh.instructions && poh.instructions.length > 0;
  }

  hasPoNotes(poh: PoHeader) {
    return poh && poh.poNotes.length > 0;
  }

  hasPodNotes(pod: PoDetail) {
    return pod && pod.poNotes.length > 0;
  }

  isOrderPod(pod: PoDetail) {
    return pod && pod.poDetailTypeId === PoDetailTypeCode.Order;
  }

  async deletePod(pod: PoDetail) {
    const poh = pod.poHeader;
    pod.entityAspect.setDeleted();
    await this.dbSaveService.saveChanges();
    await this.refreshPods(poh);
  }

  async editPoNotes(poh: PoHeader) {
    await DialogBaseComponent.open(EditNotesDialogComponent, this, {
      xHeader: poh,
    });
  }

  async editPoInstructions(poh: PoHeader) {
    const r = await DialogBaseComponent.open(EditPoInstructionsDialogComponent, this, { poHeader: poh });
    // if (r) {
    //   this.refreshDatasource();
    // }
  }

  async editPodNotes(pod: PoDetail) {
    await DialogBaseComponent.open(EditNotesDialogComponent, this, {
      xDetail: pod,
    });
  }

  receivePo(poh: PoHeader) {
    this.router.navigate(['/purchase-order-receive', poh.id]);
  }

  @wrapBusy
  async forceCancelOpenQty() {
    if (!this.canForceCancel) return;

    const checkedPohs: PoHeader[] = [];
    this.poGridOptions.apiX?.forEachNode((node) => {
      const poh = node.data as PoHeader;
      if (poh && (poh as any)._isForceCancelChecked) {
        checkedPohs.push(poh);
      }
    });

    if (checkedPohs.length === 0) {
      await this.dialogService.showOkMessage('No P/Os Selected', 'Check at least one purchase order to force cancel.');
      return;
    }

    const podsWithOpenQty: PoDetail[] = [];
    for (const poh of checkedPohs) {
      if (poh.poStatusId !== PoStatusCode.Complete) continue;
      if (!(poh as any).arePoDetailsLoaded) {
        await this.dbQueryService.getPoDetailsExpanded(poh.id);
        (poh as any).arePoDetailsLoaded = true;
      }
      poh.poDetails
        .filter((pod) => pod.orderQty - pod.getCanceledQty() - pod.getReceivedQty() > 0)
        .forEach((pod) => podsWithOpenQty.push(pod));
    }

    if (podsWithOpenQty.length === 0) {
      await this.dialogService.showOkMessage('Nothing to Cancel', 'None of the selected purchase orders have open qty to cancel.');
      return;
    }

    const totalOpen = _.sum(podsWithOpenQty.map((pod) => pod.orderQty - pod.getCanceledQty() - pod.getReceivedQty()));
    const ok = await this.dialogService.yesNoDialog(
      'Force Cancel?',
      `This will cancel ${totalOpen} open qty across ${podsWithOpenQty.length} P/O detail(s) on ${checkedPohs.length} P/O(s). Continue?`,
    );
    if (!ok) return;

    const poTrx = this.poUtilsService.createPoTrx('Force Cancel Open Qty', podsWithOpenQty.length);
    podsWithOpenQty.forEach((pod) => {
      const openQty = pod.orderQty - pod.getCanceledQty() - pod.getReceivedQty();
      this.poUtilsService.addToPoTrxHist(pod, poTrx.id);
      pod.modTs = new Date();
      pod.poTrx = poTrx;
      this.poUtilsService.createPoNote(pod, poTrx, `Force canceled open qty: ${openQty}.`);
      this.poUtilsService.createPoDetailCancels(pod, poTrx.id, openQty);
    });

    await this.dbSaveService.saveChanges();
    this.toastr.success(`Force canceled ${totalOpen} open qty across ${checkedPohs.length} P/O(s).`);

    checkedPohs.forEach((poh) => {
      (poh as any)._isForceCancelChecked = false;
      (poh as any)._isForceCancelProcessed = true;
    });
    const rowNodes = checkedPohs
      .map((poh) => this.poGridOptions.apiX?.getRowNode(poh.id.toString()))
      .filter((n): n is NonNullable<typeof n> => n != null);
    this.poGridOptions.apiX?.redrawRows({ rowNodes });

    if (this.currentPoHeader && checkedPohs.some((p) => p.id === this.currentPoHeader.id)) {
      await this.queryPoDetails(this.currentPoHeader);
    }
  }

  @wrapBusy
  async createNewPo() {
    const manufs = await EntityFinderDialog.open(ManufacturerFinderDialogComponent, this, {
      header: 'Select Manufacturer for New Purchase Order',
    });
    if (manufs.length == 0) return;
    const newPoHeader = this.createPoForManufacturer(manufs[0].id);

    if (this.poGridOptions.apiX) {
      this.poGridOptions.apiX.refreshCells();
    }

    // this.currentPoHeader = newPoHeader;
    this.replaceLocation(newPoHeader.id);
    this.refreshDatasource();
    await this.addPod(newPoHeader);

    return newPoHeader;
  }

  createPoForManufacturer(manufacturerId: number) {
    const poStruct = <PoHeader>{};
    const now = new Date();
    poStruct.poDate = now;
    poStruct.poStatusId = PoStatusCode.NewUnposted;
    poStruct.crtnTs = now;
    poStruct.shippingWarehouseId = 1; // TODO: magic number
    poStruct.manufacturerId = manufacturerId;
    const poHeader = this.uow.createEntity(PoHeader, poStruct);
    this.dbSaveService.saveSelectedChanges([poHeader]);
    return poHeader;
  }

  @wrapBusy
  async printPo(poh: PoHeader) {
    await this.queryPoDetails(poh);
    if (poh.poDetails.length == 0) {
      this.toastr.warning('Unable to print purchase order with no details', 'Cannot Proceed');
      return;
    }
    const r = await DialogBaseComponent.open(PoPrintDialogComponent, this, {
      poHeader: poh,
      canPreview: true,
    });

    this.refreshDatasource();
    return <boolean>r;
  }

  async addPod(poh: PoHeader) {
    if (this.isAccountOwnedPo(poh)) {
      await this.dialogService.showOkMessage('Cannot Add Stock Detail', 'Account-owned inventory orders cannot have stock details added.');
      return;
    }
    const wasSaved = await DialogBaseComponent.open(AddPodsDialogComponent, this, {
      poHeader: poh,
    });
    if (wasSaved) {
      await this.refreshPods(poh);
    }
  }

  private async refreshPods(poh: PoHeader) {
    await this.queryPoDetails(poh, true);
    if (this.podGroupGridOptions.apiX) {
      this.podGroupGridOptions.apiX.refreshCells();
    }
  }

  canEditPod(pod: PoDetail) {
    return pod != null && pod.poHeader != null && pod.poHeader.poStatusId !== PoStatusCode.Complete;
  }

  canDeletePod(pod: PoDetail) {
    return this.canEditPod(pod) && !this.isOrderPod(pod);
  }

  @wrapBusy
  async editPod(pod: PoDetail) {
    const wasSaved = await DialogBaseComponent.open(EditPodDialogComponent, this, {
      poHeader: pod.poHeader,
      poDetail: pod,
    });
    if (wasSaved) {
      await this.queryPoDetails(pod.poHeader, true);
      const detailGridInfo = this.podGroupGridOptions.apiX!.getDetailGridInfo('detail_' + pod.productId.toString());
      detailGridInfo?.api?.refreshCells();
    }
  }

  // Removed for now - because PO's get created during pull process ( and return to inventory/damaged)
  async createPurchaseOrders() {
    this.router.navigate(['/create-purchase-orders']);
  }

  async onTabChanged(evt: any) {
    const ix = evt.index;
    // HACK: needed because AgGrid loses buttons when tabbing from one tab to another and then back.
    this.podGroups = this.podGroups.slice(0);
    this.jobOrders = this.jobOrders.slice(0);

    // hack: because of ag-grid issues with button redraw on ngIf'd tab.
    if (ix == 1) {
      AgFns.autoSizeAllColumns(this.joGridOptions);
    }
  }

  isAccountOwnedPodGroup(podg: PodGroup): boolean {
    return podg.poDetails.some((pod) => pod.joDetail?.joHeader?.isAccountOwnedOrder);
  }

  isAccountOwnedPo(poh: PoHeader): boolean {
    return poh.poDetails?.some((pod) => pod.joDetail?.joHeader?.isAccountOwnedOrder) ?? false;
  }

  noop() {}
}
